/**
 * useOfferData Hook
 * 
 * Clean hook for offers list data management.
 * Handles fetching, filtering, pagination - components just consume.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks/redux';
import type { OfferQueryParams } from '@/app/types/offers';
import {
  fetchOffers,
  setFilters,
  setPage,
  setPageSize,
  clearFilters,
  invalidate as invalidateAction,
  selectOffers,
  selectOffersTotalCount,
  selectOffersCurrentPage,
  selectOffersPageSize,
  selectOffersFilters,
  selectOffersIsLoading,
  selectOffersError,
  selectOffersTotalPages,
  selectOffersInvalidated,
  PAGE_SIZE_OPTIONS,
  type PageSizeOption,
} from '@/app/store/offersSlice';

export function useOfferData() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const prevInvalidatedRef = useRef(false);
  
  // Select all state
  const offers = useAppSelector(selectOffers);
  const totalCount = useAppSelector(selectOffersTotalCount);
  const currentPage = useAppSelector(selectOffersCurrentPage);
  const pageSize = useAppSelector(selectOffersPageSize);
  const filters = useAppSelector(selectOffersFilters);
  const isLoading = useAppSelector(selectOffersIsLoading);
  const error = useAppSelector(selectOffersError);
  const totalPages = useAppSelector(selectOffersTotalPages);
  const invalidated = useAppSelector(selectOffersInvalidated);

  // Initial fetch on mount or when invalidated
  useEffect(() => {
    if ((offers.length === 0 || invalidated) && !isLoading && !error) {
      dispatch(fetchOffers());
    }
  }, [invalidated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when invalidated changes from false to true (not just on mount)
  useEffect(() => {
    if (invalidated && !prevInvalidatedRef.current && !isLoading && !error) {
      dispatch(fetchOffers());
    }
    prevInvalidatedRef.current = invalidated;
  }, [invalidated, isLoading, error, dispatch]);

  // Refetch when returning to list page route if invalidated
  useEffect(() => {
    if (location.pathname === '/offers' && invalidated && !isLoading && !error) {
      dispatch(fetchOffers());
    }
  }, [location.pathname, invalidated, isLoading, error, dispatch]);

  // Focus/visibility handling - refetch when page becomes visible if invalidated
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && invalidated && !isLoading && !error) {
        dispatch(fetchOffers());
      }
    };

    const handleFocus = () => {
      if (invalidated && !isLoading && !error) {
        dispatch(fetchOffers());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [invalidated, isLoading, error, dispatch]);

  // Apply filters and fetch
  const applyFilters = useCallback((newFilters: Partial<OfferQueryParams>) => {
    dispatch(setFilters(newFilters));
    dispatch(fetchOffers({ ...filters, ...newFilters, offset: 0 }));
  }, [dispatch, filters]);

  // Change page
  const goToPage = useCallback((page: number) => {
    dispatch(setPage(page));
    dispatch(fetchOffers({ ...filters, offset: (page - 1) * pageSize }));
  }, [dispatch, filters, pageSize]);

  // Refresh current view
  const refresh = useCallback(() => {
    dispatch(fetchOffers());
  }, [dispatch]);

  // Clear all filters
  const resetFilters = useCallback(() => {
    dispatch(clearFilters());
    dispatch(fetchOffers());
  }, [dispatch]);

  // Mark list as invalidated (will refetch on next mount)
  const invalidate = useCallback(() => {
    dispatch(invalidateAction());
  }, [dispatch]);

  // Change page size
  const changePageSize = useCallback((size: number) => {
    if (PAGE_SIZE_OPTIONS.includes(size as PageSizeOption)) {
      dispatch(setPageSize(size as PageSizeOption));
      dispatch(fetchOffers({ ...filters, limit: size, offset: 0 }));
    }
  }, [dispatch, filters]);

  return {
    // Data
    offers,
    totalCount,
    
    // Pagination
    currentPage,
    pageSize,
    totalPages,
    
    // Filters
    filters,
    
    // Status
    isLoading,
    error,
    
    // Actions
    applyFilters,
    goToPage,
    changePageSize,
    refresh,
    resetFilters,
    invalidate,
    
    // Constants
    pageSizeOptions: PAGE_SIZE_OPTIONS,
  };
}


